import {parentsOf} from './tree'
import type {TreeIndex} from './types'

/*
 * Кем человек приходится тому, вокруг кого построено древо.
 *
 * Считаем по ближайшему общему предку: a — сколько поколений вверх от
 * корня до него, b — сколько вверх от человека. Из пары (a, b) и выводится
 * термин. Русская система здесь неоднородна, и это не произвол:
 *
 *   a=2, b=1  дядя            — слово само по себе означает боковую ветвь,
 *   a=3, b=1  2-юродный дед   — а «дед» означает прямого предка, поэтому
 *                               без уточнения его не отличить от родного.
 *
 * Отсюда правило: у «дяди» и «племянника» приставка равна min(a, b),
 * у «деда» и «прадеда» — на единицу больше. Проверено по дереву из
 * «Древа жизни»: 2-юродный дед, 2-юродная бабушка, 2-юродный дядя,
 * 2-юродная тётя, 2-юродная племянница, 3-юродный брат — всё сходится.
 */

/** Мужчина ли. В Gramps 1 — мужской, 0 — женский, 2 — неизвестно. */
const isMale = (ix: TreeIndex, h: string) => ix.people.get(h)?.gender === 1

/** Слово в родительном падеже — для «муж 2-юродной сестры». */
const GEN: Record<string, string> = {
  'отец': 'отца', 'мать': 'матери',
  'дед': 'деда', 'бабушка': 'бабушки',
  'сын': 'сына', 'дочь': 'дочери',
  'внук': 'внука', 'внучка': 'внучки',
  'брат': 'брата', 'сестра': 'сестры',
  'дядя': 'дяди', 'тётя': 'тёти',
  'племянник': 'племянника', 'племянница': 'племянницы',
  'муж': 'мужа', 'жена': 'жены',
}

/** Склоняем обе части: «2-юродная сестра» -> «2-юродной сестры». */
const genitive = (term: string): string =>
  term.split(' ').map(w => {
    if (/ый$/.test(w)) return w.replace(/ый$/, 'ого')
    if (/ая$/.test(w)) return w.replace(/ая$/, 'ой')
    // «прапрадед» -> «прапрадеда»: приставки не мешают, режем и клеим назад
    const m = w.match(/^((?:пра)*)(.*)$/)!
    const base = GEN[m[2]]
    return base ? m[1] + base : w
  }).join(' ')

/** Прямые предки со степенью удаления. Минимум — на случай двух путей. */
function upwards(ix: TreeIndex, start: string): Map<string, number> {
  const dist = new Map<string, number>([[start, 0]])
  const queue = [start]
  for (let i = 0; i < queue.length; i++) {
    const h = queue[i]
    const g = dist.get(h)!
    for (const p of parentsOf(ix, h)) {
      if (!dist.has(p)) {
        dist.set(p, g + 1)
        queue.push(p)
      }
    }
  }
  return dist
}

/** «пра» нужное число раз: 3 -> прадед, 4 -> прапрадед. */
const ancestor = (n: number, male: boolean): string =>
  n <= 1 ? (male ? 'отец' : 'мать') : 'пра'.repeat(n - 2) + (male ? 'дед' : 'бабушка')

const descendant = (n: number, male: boolean): string =>
  n <= 1 ? (male ? 'сын' : 'дочь') : 'пра'.repeat(n - 2) + (male ? 'внук' : 'внучка')

/** «2-юродный», «3-юродная»; для родной степени приставки нет. */
const degree = (n: number, male: boolean): string =>
  n <= 1 ? '' : `${n}-юродн${male ? 'ый' : 'ая'} `

/** Термин без учёта брака: только кровное родство. */
function bloodTerm(ix: TreeIndex, root: string, h: string): string | null {
  if (h === root) return null
  const male = isMale(ix, h)
  const up = upwards(ix, root)
  const theirs = upwards(ix, h)

  let best: {a: number; b: number} | null = null
  for (const [anc, b] of theirs) {
    const a = up.get(anc)
    if (a === undefined) continue
    if (!best || a + b < best.a + best.b || (a + b === best.a + best.b && Math.abs(a - b) < Math.abs(best.a - best.b))) {
      best = {a, b}
    }
  }
  if (!best) return null

  const {a, b} = best
  if (a === 0) return descendant(b, male)
  if (b === 0) return ancestor(a, male)

  const m = Math.min(a, b)
  const d = Math.abs(a - b)

  if (d === 0) return degree(m, male) + (male ? 'брат' : 'сестра')
  if (a > b) {
    // человек в старшем колене: дядя, двоюродный дед, …
    if (d === 1) return degree(m, male) + (male ? 'дядя' : 'тётя')
    return degree(m + 1, male) + ancestor(d, male)
  }
  // человек в младшем колене: племянник, внучатый племянник, …
  if (d === 1) return degree(m, male) + (male ? 'племянник' : 'племянница')
  const great = d === 2 ? 'внучат' : 'пра'.repeat(d - 3) + 'правнучат'
  return degree(m, male) + great + (male ? 'ый племянник' : 'ая племянница')
}

const partners = (ix: TreeIndex, h: string): string[] =>
  (ix.famsOfPartner.get(h) || [])
    .map(f => (f.father_handle === h ? f.mother_handle : f.father_handle))
    .filter((x): x is string => !!x)

/* ---------- свойство: родня по браку ----------
 *
 * Русские термины свойства держатся не на расстоянии, а на том, с чьей
 * стороны родня и какого пола сам родственник. Пары ниже читаются так:
 * первое слово — если супруг ЖЕНА, второе — если МУЖ. Отец жены тесть,
 * отец мужа свёкор; сестра жены свояченица, сестра мужа золовка.
 *
 * Дальше первого круга терминов нет: у мужа троюродной сестры имени в
 * языке не существует, и придумывать его мы не будем — там остаётся
 * описание.
 */
const BY_SPOUSE: Record<string, [string, string]> = {
  'отец': ['тесть', 'свёкор'],
  'мать': ['тёща', 'свекровь'],
  'брат': ['шурин', 'деверь'],
  'сестра': ['свояченица', 'золовка'],
  // ребёнок супруга, но не свой: кровное родство проверено раньше
  'сын': ['пасынок', 'пасынок'],
  'дочь': ['падчерица', 'падчерица'],
}

/** Супруг кровного родственника: слово зависит от того, кто этот
 *  родственник и какого пола сам пришедший в семью. */
const BY_BLOOD: Record<string, [string, string]> = {
  // [если родственник мужчина, если женщина] — по полу СУПРУГА
  'дочь': ['зять', ''],
  'сестра': ['зять', ''],
  'сын': ['', 'невестка'],
  'брат': ['', 'невестка'],
  // муж матери и жена отца, когда сами они не родители: отчим и мачеха
  'мать': ['отчим', ''],
  'отец': ['', 'мачеха'],
}

const cap = (s: string) => (s ? s[0].toUpperCase() + s.slice(1) : s)

const pair = (p: [string, string] | undefined, i: number) => (p ? p[i] : '')

/* Расшифровку даём только к настоящему слову. Когда слова в языке нет и
   в карточке стоит само описание, повторять его подсказкой незачем. */
const named = (word: string, plain: string): Named =>
  word ? {term: cap(word), plain} : {term: cap(plain), plain: ''}

interface Named {
  /** слово, если оно в языке есть: «Золовка» */
  term: string
  /** и объяснение к нему: «сестра мужа» */
  plain: string
}

/**
 * Родня по браку. Возвращает и слово, и расшифровку.
 *
 * Расшифровка нужна не для полноты: «шурин» и «деверь» путают даже те,
 * кто их знает, и без пояснения слово в карточке скорее ставит в тупик,
 * чем помогает. Поэтому термин показываем, а объяснение держим рядом.
 */
function inLaw(ix: TreeIndex, root: string, h: string): Named | null {
  const male = isMale(ix, h)
  const mine = partners(ix, root)

  // 1. кровная родня супруга: тесть, тёща, шурин, золовка…
  for (const sp of mine) {
    const via = bloodTerm(ix, sp, h)
    if (!via) continue
    const wife = !isMale(ix, sp)
    const plain = `${via} ${wife ? 'жены' : 'мужа'}`
    const word = pair(BY_SPOUSE[via], wife ? 0 : 1)
    return named(word, plain)
  }

  // 2. супруг кровного родственника: зять, невестка, отчим, мачеха…
  for (const sp of partners(ix, h)) {
    const via = bloodTerm(ix, root, sp)
    if (!via) continue
    const plain = `${male ? 'муж' : 'жена'} ${genitive(via)}`
    const word = pair(BY_BLOOD[via], male ? 0 : 1)
    return named(word, plain)
  }

  // 3. свояк: муж сестры жены. Слово есть только для мужчин; для жены
  //    брата мужа язык предлагает «ятровку», которой никто не пользуется.
  for (const sp of mine) {
    for (const sib of partners(ix, h)) {
      const via = bloodTerm(ix, sp, sib)
      if (via !== 'сестра' && via !== 'брат') continue
      const plain = `${male ? 'муж' : 'жена'} ${via === 'сестра' ? 'сестры' : 'брата'} ` +
                    `${isMale(ix, sp) ? 'мужа' : 'жены'}`
      return named(male && via === 'сестра' && !isMale(ix, sp) ? 'свояк' : '', plain)
    }
  }

  // 4. сват и сватья: родители мужа дочери или жены сына
  for (const kid of (ix.famsOfPartner.get(root) || []).flatMap(f => (f.child_ref_list || []).map(c => c.ref))) {
    for (const kidSpouse of partners(ix, kid)) {
      if (!parentsOf(ix, kidSpouse).includes(h)) continue
      const plain = `${male ? 'отец' : 'мать'} ${isMale(ix, kidSpouse) ? 'мужа дочери' : 'жены сына'}`
      return named(male ? 'сват' : 'сватья', plain)
    }
  }

  return null
}

/**
 * Кем этот человек приходится корню древа — словом и расшифровкой.
 *
 * Кровное родство расшифровки не требует: «двоюродный дядя» понятен сам
 * по себе. Свойство — требует: «шурин» и «деверь» путают даже те, кто их
 * знает, и слово без пояснения скорее ставит в тупик.
 */
export function kinshipOf(ix: TreeIndex, root: string, h: string): Named {
  if (h === root) return {term: '', plain: ''}

  const own = bloodTerm(ix, root, h)
  if (own) return {term: cap(own), plain: ''}

  const male = isMale(ix, h)
  if (partners(ix, root).includes(h)) return {term: male ? 'Муж' : 'Жена', plain: ''}

  return inLaw(ix, root, h) || {term: '', plain: ''}
}

/** Только слово — для мест, где расшифровку показывать негде. */
export const kinship = (ix: TreeIndex, root: string, h: string): string =>
  kinshipOf(ix, root, h).term
